// Rolling Z-Score normalization for an array of numbers
export function rollingZScore(arr, window) {
  const result = [];

  for (let i = 0; i < arr.length; i++) {
    if (i < window - 1 || typeof arr[i] !== 'number' || isNaN(arr[i])) {
      result.push(null);
      continue;
    }

    const slice = arr
      .slice(i - window + 1, i + 1)
      .filter((x) => typeof x === 'number' && !isNaN(x));

    if (slice.length < window) {
      result.push(null);
      continue;
    }

    const mean = slice.reduce((a, b) => a + b, 0) / slice.length;

    const std = Math.sqrt(
      slice.reduce((sum, val) => sum + Math.pow(val - mean, 2), 0) /
        slice.length
    );

    const z = (arr[i] - mean) / (std + 1e-10);
    result.push(z);
  }

  return result;
}

// Rolling Min-Max normalization for an array of numbers
export function rollingMinMax(arr, window) {
  const result = [];

  for (let i = 0; i < arr.length; i++) {
    if (i < window - 1 || typeof arr[i] !== 'number' || isNaN(arr[i])) {
      result.push(null);
      continue;
    }

    const slice = arr
      .slice(i - window + 1, i + 1)
      .filter((x) => typeof x === 'number' && !isNaN(x));

    if (slice.length < window) {
      result.push(null);
      continue;
    }

    const min = Math.min(...slice);
    const max = Math.max(...slice);
    const norm = (arr[i] - min) / (max - min + 1e-10);

    result.push(norm);
  }

  return result;
}

// Generic async wait helper used by assistant run polling
export function wait(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

// Finds the date/time field in a normalized data row
export function getDateField(row = {}) {
  return Object.keys(row || {}).find((key) => {
    const lower = key.toLowerCase();
    return lower.includes('date') || lower.includes('time');
  });
}

// Finds numeric fields in a normalized data row
export function getNumericFields(row = {}) {
  return Object.keys(row || {}).filter((key) => typeof row[key] === 'number');
}

// Returns the first and last available dates in a dataset
export function getAvailableDateRange(data = []) {
  if (!Array.isArray(data) || data.length === 0) {
    return {
      earliest: null,
      latest: null,
    };
  }

  const dateField = getDateField(data[0]);

  return {
    earliest: dateField ? data[0]?.[dateField] : null,
    latest: dateField ? data[data.length - 1]?.[dateField] : null,
  };
}

// Calculates basic statistics for all numeric fields in a dataset
export function calculateBasicStats(data = []) {
  if (!Array.isArray(data) || data.length === 0) {
    return {};
  }

  const numericFields = getNumericFields(data[0]);
  const stats = {};

  numericFields.forEach((field) => {
    const values = data
      .map((item) => item[field])
      .filter((val) => val != null && !Number.isNaN(val));

    if (values.length === 0) return;

    const first = values[0];
    const last = values[values.length - 1];

    stats[field] = {
      current: last,
      average: values.reduce((a, b) => a + b, 0) / values.length,
      min: Math.min(...values),
      max: Math.max(...values),
      first,
      last,
      change: last - first,
      trend:
        values.length > 1 && first !== 0
          ? ((last - first) / first) * 100
          : 0,
    };
  });

  return stats;
}

// Extracts readable text from an OpenAI Assistant message object
export function extractAssistantText(message) {
  return (
    message?.content
      ?.filter((contentBlock) => contentBlock.type === 'text')
      .map((contentBlock) => contentBlock.text?.value || '')
      .join('\n')
      .trim() || ''
  );
}